import { oc } from "@orpc/contract";
import { z } from "zod";

const CONTROL_CHARS_RE = new RegExp("[\\u0000-\\u001f\\u007f]");

const ColumnNameSchema = z
  .string()
  .min(1)
  .max(100)
  .refine((v) => !CONTROL_CHARS_RE.test(v), {
    message: "Column names must not contain control characters",
  });

const ColumnTypeSchema = z.enum([
  "string",
  "integer",
  "float",
  "boolean",
  "date",
]);

const ColumnDefinitionSchema = z.object({
  name: ColumnNameSchema,
  type: ColumnTypeSchema,
  description: z.string().min(10).max(500),
});

const ValidationErrorSchema = z.object({
  row_index: z.number().int().min(-1),
  column: z.string(),
  value: z.string().nullable(),
  expected_type: z.string(),
  error: z.string(),
});

const IngestModeSchema = z.enum(["snapshot", "upsert"]);

const ResolvedFrameStatusSchema = z.enum([
  "auto_resolved",
  "needs_review",
  "partial",
  "reviewed",
]);

const ColumnMatchSourceSchema = z.enum(["cache", "llm", "unmatched"]);

const ColumnMatchSchema = z.object({
  schema_column: z.string(),
  input_column: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  source: ColumnMatchSourceSchema,
});

const VersionSchema = z.object({
  id: z.uuid(),
  tableDefinitionId: z.uuid(),
  versionNumber: z.number().int().min(1),
  parentVersionId: z.uuid().nullable(),
  mostRecentFrameId: z.uuid().nullable(),
  createdBy: z.uuid(),
  keyColumn: z.string(),
  columns: z.array(ColumnDefinitionSchema),
  createdAt: z.iso.datetime(),
  recastValidationErrors: z.array(ValidationErrorSchema),
});

const DefinitionSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  createdBy: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  currentVersion: VersionSchema,
});

const FrameCellValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

const ResolvedFrameSchema = z.object({
  id: z.uuid(),
  rawFrameId: z.uuid(),
  frameNumber: z.number().int().min(1),
  versionId: z.uuid(),
  versionNumber: z.number().int().min(1),
  rowCount: z.number().int().min(0),
  columnCount: z.number().int().min(1),
  columns: z.array(z.string()),
  types: z.array(ColumnTypeSchema),
  rows: z.array(z.array(FrameCellValueSchema)),
  status: ResolvedFrameStatusSchema,
  columnMatches: z.array(ColumnMatchSchema),
  inputHeader: z.array(z.string()),
  validationErrors: z.array(ValidationErrorSchema),
  resolvedAt: z.iso.datetime(),
});

const RowDataSchema = z.record(z.string(), FrameCellValueSchema);

const LookupResultSchema = z.object({
  lookupKey: z.string(),
  rowData: RowDataSchema,
  frameNumber: z.number().int().min(1),
  versionNumber: z.number().int().min(1),
  resolvedAt: z.iso.datetime(),
});

const BatchLookupSchema = z.object({
  results: z.array(z.object({ lookupKey: z.string(), rowData: RowDataSchema })),
  notFound: z.array(z.string()),
  frameNumber: z.number().int().min(0),
  versionNumber: z.number().int().min(0),
  resolvedAt: z.iso.datetime(),
});

const IngestResultSchema = z.object({
  rawFrameId: z.uuid(),
  frameNumber: z.number().int().min(1),
  status: z.enum(["processed", "failed"]),
  mode: IngestModeSchema,
  resolvedFrameId: z.uuid().nullable(),
  resolvedFrameStatus: ResolvedFrameStatusSchema.nullable(),
  rowCount: z.number().int().min(0).nullable(),
  columnCount: z.number().int().min(1).nullable(),
  columnMatches: z.array(ColumnMatchSchema),
  validationErrors: z.array(ValidationErrorSchema),
  failureReason: z.string().nullable(),
});

const FrameHistoryEntrySchema = z.object({
  rawFrameId: z.uuid(),
  frameNumber: z.number().int().min(1),
  status: z.enum(["pending", "processed", "failed"]),
  resolvedFrameStatus: ResolvedFrameStatusSchema.nullable(),
  mode: IngestModeSchema,
  rowCount: z.number().int().min(0).nullable(),
  columnCount: z.number().int().min(1).nullable(),
  workflowRunId: z.uuid(),
  resolvedFrameId: z.uuid().nullable(),
  validationErrorCount: z.number().int().min(0),
  createdAt: z.iso.datetime(),
});

export const statefulTablesContracts = {
  list: oc
    .input(
      z.object({
        page: z.number().int().min(1).optional(),
        perPage: z.number().int().min(1).max(100).optional(),
      }),
    )
    .output(
      z.object({
        results: z.array(DefinitionSchema),
        page: z.number().int().min(1),
        perPage: z.number().int().min(1),
        total: z.number().int().min(0),
      }),
    )
    .route({ method: "GET" }),

  get: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ definition: DefinitionSchema }))
    .route({ method: "GET" }),

  create: oc
    .input(
      z.object({
        name: z.string().min(1).max(255),
        description: z.string().max(2000).optional(),
        columns: z.array(ColumnDefinitionSchema).min(1).max(100),
        keyColumn: ColumnNameSchema,
      }),
    )
    .output(z.object({ definition: DefinitionSchema }))
    .route({ method: "POST" }),

  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),

  createVersion: oc
    .input(
      z.object({
        id: z.uuid(),
        columns: z.array(ColumnDefinitionSchema).min(1).max(100),
        keyColumn: ColumnNameSchema,
      }),
    )
    .output(
      z.object({
        version: VersionSchema,
      }),
    )
    .route({ method: "POST" }),

  getLatestFrame: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ frame: ResolvedFrameSchema }))
    .route({ method: "GET" }),

  listFrames: oc
    .input(
      z.object({
        id: z.uuid(),
        page: z.number().int().min(1).optional(),
        perPage: z.number().int().min(1).max(100).optional(),
      }),
    )
    .output(
      z.object({
        results: z.array(FrameHistoryEntrySchema),
        page: z.number().int().min(1),
        perPage: z.number().int().min(1),
        total: z.number().int().min(0),
      }),
    )
    .route({ method: "GET" }),

  lookupByKey: oc
    .input(z.object({ id: z.uuid(), key: z.string().min(1) }))
    .output(z.object({ lookup: LookupResultSchema }))
    .route({ method: "GET" }),

  lookupByKeys: oc
    .input(
      z.object({
        id: z.uuid(),
        keys: z.array(z.string().min(1)).min(1).max(100),
      }),
    )
    .output(z.object({ result: BatchLookupSchema }))
    .route({ method: "POST" }),

  confirmMatches: oc
    .input(
      z.object({
        id: z.uuid(),
        frameNumber: z.number().int().min(1),
        matches: z
          .array(
            z.object({
              schema_column: z.string().min(1),
              input_column: z.string().nullable(),
            }),
          )
          .min(1),
      }),
    )
    .output(z.object({ frame: ResolvedFrameSchema }))
    .route({ method: "POST" }),
} as const;

export type StatefulTableDefinitionResponse = z.infer<typeof DefinitionSchema>;
export type StatefulTableVersionResponse = z.infer<typeof VersionSchema>;
export type StatefulTableFrameResponse = z.infer<typeof ResolvedFrameSchema>;
export type StatefulTableIngestResponse = z.infer<typeof IngestResultSchema>;
export type StatefulTableLookupResponse = z.infer<typeof LookupResultSchema>;
export type StatefulTableColumnMatch = z.infer<typeof ColumnMatchSchema>;
export type StatefulTableResolvedFrameStatus = z.infer<
  typeof ResolvedFrameStatusSchema
>;
export type StatefulTableFrameHistoryEntry = z.infer<
  typeof FrameHistoryEntrySchema
>;
